import { useCallback, useEffect, useState } from "react";

import { clearSavBlock, getSavBlockedUntil, isSavBlocked, setSavBlock } from "@jti/fe-common";

type UseSavBlockingControllerParams = {
  isBlockingEnabled: boolean;
  blockingMinutes: number;
};

type UseSavBlockingControllerResult = {
  isBlocked: boolean;
  isUnderage: boolean;
  setIsUnderage: (value: boolean) => void;
  restoreActiveBlock: () => boolean;
  triggerBlock: () => void;
};

const SAV_BLOCKING_POLL_INTERVAL_MS = 1000;

export const useSavBlockingController = ({
  isBlockingEnabled,
  blockingMinutes,
}: UseSavBlockingControllerParams): UseSavBlockingControllerResult => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isUnderage, setIsUnderage] = useState(false);

  // Purge any stale block when the blocking SAV is disabled.
  useEffect(() => {
    if (!isBlockingEnabled) {
      setIsBlocked(false);
      setIsUnderage(false);

      if (isSavBlocked()) {
        clearSavBlock();
      }
    }
  }, [isBlockingEnabled]);

  // Poll the persisted timestamp so the form reappears when the block expires.
  useEffect(() => {
    if (!isBlockingEnabled || !isBlocked) {
      return;
    }

    const unblockWhenExpired = () => {
      const blockedUntil = getSavBlockedUntil();
      if (!blockedUntil || Date.now() >= blockedUntil) {
        clearSavBlock();
        setIsBlocked(false);
      }
    };

    unblockWhenExpired();
    const timer = setInterval(unblockWhenExpired, SAV_BLOCKING_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isBlockingEnabled, isBlocked]);

  // Restores an active block on mount, returns whether the user is blocked.
  const restoreActiveBlock = useCallback(() => {
    if (isBlockingEnabled && isSavBlocked()) {
      setIsBlocked(true);
      return true;
    }
    return false;
  }, [isBlockingEnabled]);

  const triggerBlock = useCallback(() => {
    setSavBlock(blockingMinutes);
    setIsBlocked(true);
  }, [blockingMinutes]);

  return {
    isBlocked,
    isUnderage,
    setIsUnderage,
    restoreActiveBlock,
    triggerBlock,
  };
};
